import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Pagination component for admin lists
 * @param {number} currentPage - Current page number (1-indexed)
 * @param {number} totalPages - Total number of pages
 * @param {number} totalItems - Total number of items
 * @param {number} perPage - Items per page
 * @param {function} onPageChange - Callback when page changes
 * @param {function} onViewAll - Optional callback for "View All" button
 * @param {boolean} showViewAll - Whether to show "View All" button
 */
export default function Pagination({ 
  currentPage, 
  totalPages, 
  totalItems, 
  perPage,
  onPageChange,
  onViewAll,
  showViewAll = true
}) {
  const startItem = (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, totalItems);

  if (totalItems === 0) return null;

  return (
    <div className="flex items-center justify-between flex-wrap gap-4 pt-4 border-t border-white/10">
      {/* Items info */}
      <div className="text-sm text-zinc-500">
        Showing <span className="text-white font-semibold">{startItem}-{endItem}</span> of{' '}
        <span className="text-white font-semibold">{totalItems}</span> items
      </div>

      {/* Page controls */}
      <div className="flex items-center gap-2">
        {/* View All button */}
        {showViewAll && totalPages > 1 && (
          <Button
            variant="outline"
            size="sm"
            onClick={onViewAll}
            className="border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/10 mr-2"
          >
            View All
          </Button>
        )}

        {/* First page */}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 border-white/10 disabled:opacity-30"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
          <ChevronsLeft className="w-4 h-4" />
        </Button>

        {/* Previous page */}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 border-white/10 disabled:opacity-30"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {generatePageNumbers(currentPage, totalPages).map((page, idx) => (
            page === '...' ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-zinc-500">...</span>
            ) : (
              <Button
                key={page}
                variant={page === currentPage ? "default" : "outline"}
                size="sm"
                className={`h-8 w-8 ${
                  page === currentPage 
                    ? 'bg-[#FFD700] text-black font-bold hover:bg-[#E5C100]' 
                    : 'border-white/10 hover:border-[#FFD700]/50'
                }`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </Button>
            )
          ))}
        </div>

        {/* Next page */}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 border-white/10 disabled:opacity-30"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>

        {/* Last page */}
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8 border-white/10 disabled:opacity-30"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
        >
          <ChevronsRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/**
 * Generate array of page numbers to display
 */
function generatePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = [];
  
  // Always show first page
  pages.push(1);
  
  if (currentPage > 3) {
    pages.push('...');
  }
  
  // Show pages around current page
  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }
  
  if (currentPage < totalPages - 2) {
    pages.push('...');
  }
  
  // Always show last page
  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }
  
  return pages;
}
